import cx from 'classnames'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import ButtonCollection from 'components/ButtonCollection'
import ErrorBlock from 'components/ErrorBlock'
import Spinner from 'components/Spinner'
import { useCallRootViewModel } from 'contexts/CallRootContext'
import { VideoCall, VideoCallStatus } from 'messaging/calling/VideoCall'
import {
  VideoCallParticipant,
  VideoCallParticipantStatus,
} from 'messaging/calling/VideoCallParticipant'
import CallViewModel, {
  CallOperatorState,
  ViewMode,
} from 'messaging/calling/CallViewModel'
import { CallStatusInfo } from 'messaging/calling/views/CallPrompt/CallStatusInfo'
import { observer } from 'mobx-react'
import * as React from 'react'
import styles from './CallPrompt.sass'

interface CallPromptProps {
  call: VideoCall
  bordered?: boolean
  shadowed?: boolean
  viewToggle?: boolean
}

/**
 * The CallPrompt
 */
export default observer(function CallPrompt({
  call,
  bordered,
  shadowed,
  viewToggle,
}: CallPromptProps) {
  const vm = useCallRootViewModel().callViewModelFor(call)

  return (
    <div
      className={cx(
        styles.root,
        bordered && styles.bordered,
        shadowed && styles.shadowed
      )}
    >
      {vm.fetch.match({
        pending: () => (
          <div className={styles.fetching}>
            <Spinner />
          </div>
        ),
        rejected: (err) => <ErrorBlock error={err} />,
        resolved: () => (
          <>
            <div className={styles.main}>
              <CallStatusInfo vm={vm} />
            </div>
            <div className={styles.menu}>
              <CallParticipants vm={vm} />
              <CallActions vm={vm} viewToggle={viewToggle} />
            </div>
          </>
        ),
      })}
    </div>
  )
})

interface CallParticipantsProps {
  vm: CallViewModel
}

/**
 " Call Participants
 */
export const CallParticipants = observer(function CallParticipants({
  vm,
}: CallParticipantsProps) {
  const participants = vm.call.participants
  return (
    <div className={styles.participants}>
      {participants.length > 4 ? (
        <span className={styles.participantCount}>
          {participants.length} participants
        </span>
      ) : (
        participants.map((p) => (
          <div
            key={p.userId}
            className={cx(
              styles.participant,
              p.status !== VideoCallParticipantStatus.Connected &&
                styles.disconnected
            )}
          >
            <Avatar size={38} subject={p.member} />
          </div>
        ))
      )}
    </div>
  )
})

interface CallActionsProps {
  vm: CallViewModel
  viewToggle?: boolean
}

/**
 " Call Actions
 */
export const CallActions = observer(function CallActions({
  vm,
  viewToggle,
}: CallActionsProps) {
  if (vm.call.callStatus !== VideoCallStatus.InProgress) {
    return <JoinedReport vm={vm} />
  }

  switch (vm.state) {
    case CallOperatorState.Inactive: {
      return (
        <div className={styles.actions}>
          <ButtonCollection>
            <Button
              loading={vm.join.pending}
              disabled={vm.join.pending}
              type="primary"
              onClick={vm.join}
            >
              Join
            </Button>
          </ButtonCollection>
        </div>
      )
    }
    case CallOperatorState.Incoming:
      return (
        <div className={styles.actions}>
          <ButtonCollection>
            <Button onClick={vm.decline}>Decline</Button>
            <Button type="primary" onClick={vm.join}>
              Accept
            </Button>
          </ButtonCollection>
        </div>
      )
    case CallOperatorState.Connecting:
      return (
        <div className={styles.actions}>
          <Spinner size={16} thickness={2} />
        </div>
      )
    case CallOperatorState.Connected:
      return (
        <div className={styles.actions}>
          <ButtonCollection>
            <Button type="danger" onClick={vm.hangup}>
              Hang up
            </Button>
            {viewToggle &&
              (vm.viewMode === ViewMode.Hidden ? (
                <Button onClick={vm.show}>Show</Button>
              ) : (
                <Button onClick={vm.hide}>Hide</Button>
              ))}
          </ButtonCollection>
        </div>
      )
  }
})

interface JoinedReportProps {
  vm: CallViewModel
}

/**
 " Joined Report.
 * WARNING: Here be dragons.
 */
export const JoinedReport = observer(function JoinedReport({
  vm,
}: JoinedReportProps) {
  let result: string
  const joined = vm.call.participants.filter(
    (p) => p.status === VideoCallParticipantStatus.Disconnected
  )
  const missed = vm.call.participants.filter(
    (p) => p.status === VideoCallParticipantStatus.Missed
  )
  const declined = vm.call.participants.filter(
    (p) => p.status === VideoCallParticipantStatus.Declined
  )
  const joinedCount = joined.length
  // If everyone is `Disconnected` then everyone joined.
  if (joinedCount === vm.call.participants.length) {
    if (joinedCount === 2 && joined.some((p) => p.member.isCurrentUser)) {
      result = 'You both joined'
    } else {
      result = 'Everyone joined'
    }
  } else {
    // Don't include ourselves for the Joined segment.
    const otherJoined = joined.filter((p) => p.member.isCurrentUser === false)
    result =
      [
        otherJoined.length > 1 && `${toPeople(otherJoined)} joined`,
        missed.length > 0 && `${toPeople(missed)} missed the call`,
        declined.length > 0 && `${toPeople(declined)} declined the call`,
      ]
        .filter(Boolean)
        .join('. ') + '.'
  }

  const tooltip = (
    `Joined: ${joined.map((p) => p.member.displayName).join(', ')}\n\n` +
    (declined.length > 0
      ? `Declined: ${declined.map((p) => p.member.displayName).join(', ')}\n\n`
      : '') +
    (missed.length > 0
      ? `Missed: ${missed.map((p) => p.member.displayName).join(', ')}`
      : '')
  ).trim()

  return (
    <div className={styles.joinedReport} title={tooltip}>
      {result}
    </div>
  )
})

/**
 * Returns the display name of the only participant in the list,
 * or the total count if the count is not exactly 1.
 * @param list
 */
function toPeople(list: readonly VideoCallParticipant[]) {
  if (list.length !== 1) {
    return `${list.length.toString()} people`
  } else {
    const member = list[0].member
    return member.isCurrentUser ? 'You' : member.displayName
  }
}
